import { autoinject } from "aurelia-framework";
import { RequestConstants } from "@rightship-tech/common-ui/constants/request.constants";
import { Urls } from "../../constants/mep/mep.constants";
import { FileHelper } from "../../helpers/file-helper";
import {
    DownloadEmissionsDataRequest,
    GetMepEmissionsAdditionalDetailsRequest,
    GetMepEmissionsAdditionalDetailsResponse,
    GetMepEmissionsByMonthResponse,
    MepCustomer,
    MepDashboardRequest,
    MepDashboardSummaryResponse,
    MepGetDashboardEmissionsBySomeTypeRequest,
    MepGetDashboardPointsOfInterestHeatmapResponse,
    MepGetDashboardVesselCallsByVesselTypeResponse,
    MepGetDashboardVesselStatisticsRequest,
    MepGetDashboardVesselStatisticsResponse,
    MepGetEmissionsBySomeTypeResponse,
    MepGetMonthsListResponse,
    MepGetVesselGroupsResponse,
    MepPortDetails,
    MepPortMenuItem,
    MepTopVesselsEmissionsPerformers,
    MepUpdateCargoDataRequest,
    MepUploadVesselPortActivitiesRequest
} from "../../models/mep/mep.models";
import { ConfigurationService } from "@rightship-tech/common-ui/services/basic/configuration.service";
import { GetVesselEmissionsRequest } from "../../views/pages/modules/mep/models/mep-port-models";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
export class MepService {

    private _allPortDetails: { [id: string]: MepPortDetails; } = {};

    constructor(private requestClient: RequestClientService, private configurationService: ConfigurationService) {
    }

    public getMepMonthsList(portId: number, year: number): Promise<MepGetMonthsListResponse> {
        return this.requestClient.get<MepGetMonthsListResponse>(`${Urls.GetMepMonthsList}/${portId}/${year}`, {});
    }

    public getMepCustomers(): Promise<MepCustomer[]> {
        return this.requestClient.get<MepCustomer[]>(Urls.GetMepCustomers, {});
    }

    public getMepPorts(customerId: number): Promise<MepPortMenuItem[]> {
        return this.requestClient.get<MepPortMenuItem[]>(`${Urls.GetMepPorts}/${customerId}`, {});
    }

    public getVesselTypeGroups(portId: number, year: number, endpoint?: string): Promise<MepGetVesselGroupsResponse> {
        return this.requestClient.get<MepGetVesselGroupsResponse>(endpoint ? endpoint : `${Urls.GetVesselTypeGroups}/${portId}/${year}`, {});
    }

    public getVesselTypeGroupsForPortHome(portId: number, year: number): Promise<MepGetVesselGroupsResponse> {
        return this.getVesselTypeGroups(portId, year, Urls.GetVesselTypeGroupsForPortHome);
    }

    public getVesselTypeGroupsForPortDashboard(portId: number, year: number): Promise<MepGetVesselGroupsResponse> {
        return this.getVesselTypeGroups(portId, year, Urls.GetVesselTypeGroupsForPortDashboard);
    }

    public getMepPortDetails(portId: number, year: number, getFromCache: boolean): Promise<MepPortDetails> {
        //if the caller is ok with a cached value then see if we have one
        const key = portId + '-' + year
        if (getFromCache && this._allPortDetails[key]) {
            return new Promise<MepPortDetails>((resolve) => {
                resolve(this._allPortDetails[key]);
            });
        } else {

            const promise: Promise<MepPortDetails> = this.requestClient.get<MepPortDetails>(`${Urls.GetMepPortDetails}/${portId}/${year}`, {});
            promise.then(x => {
                this._allPortDetails[key] = x;
            });
            return promise;
        }
    }

    public getMepDashboardSummaryForPortDashboard(request: MepDashboardRequest): Promise<MepDashboardSummaryResponse> {
        return this.requestClient.post<MepDashboardSummaryResponse>(`${Urls.GetMepDashboardSummaryForPortDashboard}`, {}, request);
    }

    public getMepVesselStatistics(request: MepGetDashboardVesselStatisticsRequest): Promise<MepGetDashboardVesselStatisticsResponse> {
        return this.requestClient.post<MepGetDashboardVesselStatisticsResponse>(`${Urls.GetMepVesselStatistics}`, {}, request);
    }

    public getMepTopVesselsEmissionsPerformersForPortDashboard(request: MepDashboardRequest): Promise<MepTopVesselsEmissionsPerformers[]> {
        return this.requestClient.post<MepTopVesselsEmissionsPerformers[]>(Urls.GetMepTopVesselsEmissionsPerformers, {}, request);
    }

    public getMepEmissionsByMonth(request: MepDashboardRequest): Promise<GetMepEmissionsByMonthResponse> {
        return this.requestClient.post<GetMepEmissionsByMonthResponse>(`${Urls.GetMepEmissionsByMonth}`, {}, request);
    }

    public getMepEmissionsBySomeType(request: MepGetDashboardEmissionsBySomeTypeRequest): Promise<MepGetEmissionsBySomeTypeResponse> {
        return this.requestClient.post<MepGetEmissionsBySomeTypeResponse>(`${Urls.GetMepEmissionsBySomeType}`, {}, request);
    }

    public getMepEmissionsAdditionalDetails(request: GetMepEmissionsAdditionalDetailsRequest, endpoint?: string): Promise<GetMepEmissionsAdditionalDetailsResponse> {
        return this.requestClient.post<GetMepEmissionsAdditionalDetailsResponse>(endpoint ? endpoint : `${Urls.GetMepEmissionsAdditionalDetails}`, {}, request);
    }

    public getMepEmissionsAdditionalDetailsForPortDashboard(request: GetMepEmissionsAdditionalDetailsRequest): Promise<GetMepEmissionsAdditionalDetailsResponse> {
        return this.getMepEmissionsAdditionalDetails(request, Urls.GetMepEmissionsAdditionalDetailsForPortDashboard);
    }

    public getMepDashboardVesselTypeDistribution(request: MepDashboardRequest): Promise<MepGetDashboardVesselCallsByVesselTypeResponse> {
        return this.requestClient.post<MepGetDashboardVesselCallsByVesselTypeResponse>(`${Urls.GetMepDashboardVesselTypeDistribution}`, {}, request);
    }

    public getMepDashboardPointsOfInterestHeatmap(request: MepDashboardRequest): Promise<MepGetDashboardPointsOfInterestHeatmapResponse> {
        return this.requestClient.post<MepGetDashboardPointsOfInterestHeatmapResponse>(`${Urls.GetMepDashboardPointsOfInterestHeatmap}`, {}, request);
    }

    public uploadMepVesselPortActivities(request: MepUploadVesselPortActivitiesRequest): Promise<any> {
        return this.requestClient.postFormData<any>(`${Urls.UploadMepVesselPortActivities}`, request);
    }

    public updateCargoData(request: MepUpdateCargoDataRequest): Promise<any> {
        return this.requestClient.post<any>(`${Urls.UpdateCargoData}`, {}, request);
    }

    public downloadMepVessel(request: GetVesselEmissionsRequest): Promise<void> {
        return this.requestClient.post<any>(`${Urls.DownloadMepVessel}`, {}, request);
    }

    public downloadMepVesselPortActivitiesTemplate(request: GetVesselEmissionsRequest, portName: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            try {
                const url = `${ConfigService.instance().config.urls.rightShipPlatformApiUrl}/api/v1/${Urls.DownloadMepVesselPortActivitiesTemplate}`;

                $.ajax({
                    type: "POST",
                    url: url,
                    contentType: "application/json",
                    data: JSON.stringify(request),
                    xhrFields: {
                        withCredentials: true
                    },
                    beforeSend: (request) => {
                        const headers = this.configurationService.getList(RequestConstants.ConfigurationTypes.RequestHeaders);
                        const keys = Object.keys(headers);
                        keys.forEach(key => {
                            request.setRequestHeader(key, headers[key].toString());
                        });
                    },
                    success: x => {
                        const url = window.URL.createObjectURL(x);
                        FileHelper.downloadFile(url, `Mep Vessels Port Activities Template (${portName}).csv`);

                        setTimeout(() => {
                            window.URL.revokeObjectURL(url);
                            resolve();
                        });
                    },
                    error: () => {
                        reject();
                    },
                    xhr: () => {
                        const xhr = new XMLHttpRequest();
                        xhr.responseType = 'blob'
                        return xhr;
                    }
                });
            }
            catch (exception) {
                resolve();
            }
        })
    }

    public downloadEmissionsData(request: DownloadEmissionsDataRequest): Promise<any> {
        return this.requestClient.post<any>(`${Urls.DownloadEmissionsData}`, {}, request);
    }
}

